import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import CloseIcon from '@material-ui/icons/Close';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';

import * as SingleUseCodeService from '../../api/singleUseCodeService';
import * as StampCampaignService from '../../api/stampCampaignService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  logoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  qr: {
    margin: theme.spacing(),
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  formControl: {
    minWidth: 250,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  stampNum: number;
  storeId: string;
  stampCampaignList: StampCampaignService.StampCampaign[];
};

export default function DynamicQRCodeDialog({
  open,
  onClose,
  stampNum,
  storeId,
  stampCampaignList,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const [deeplink, setDeeplink] = useState('');
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(
    stampCampaignList.length === 1 ? stampCampaignList[0].slotNumber : 0
  );
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (company && selectedSlotNumber > 0) {
        try {
          const res = await SingleUseCodeService.generate({
            companyId: company.id,
            numOfStamps: stampNum,
            slotNumber: selectedSlotNumber,
            storeId,
          });

          setDeeplink(res.deepLink);
        } catch (e: any) {
          console.log('Error: ', e);
        }
      }
    };
    if (selectedSlotNumber > 0) {
      fetch();
    }
  }, [stampNum, company, storeId, selectedSlotNumber]);

  const handleSelectStampCampaign = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedSlotNumber(event.target.value as number);
    setSelectOpen(!selectOpen);
  };

  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <div className={classes.box}>
        <div className={classes.close}>
          <IconButton
            onClick={() => {
              setDeeplink('');
              onClose();
            }}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      <div className={classes.logoContainer}>
        <img
          src={company.logo?.versions.xl}
          className={classes.merchantLogo}
          alt="flex reward"
        />
        {stampCampaignList.length > 1 && (
          <FormControl className={classes.formControl}>
            <Select
              value={selectedSlotNumber}
              onChange={handleSelectStampCampaign}
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'selectedSlotNumber' }}
              onOpen={() => setSelectOpen(!selectOpen)}
              displayEmpty
            >
              {selectedSlotNumber === 0 && !selectOpen && (
                <MenuItem value={0}>{'Select Card'}</MenuItem>
              )}
              {stampCampaignList.map((s) => (
                <MenuItem value={s.slotNumber}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {selectedSlotNumber > 0 && (
          <>
            <div className={classes.qr}>
              <QRCode
                value={deeplink}
                size={350}
                renderAs="svg"
                includeMargin
              />
            </div>
            <Typography variant="h4" className={classes.header}>
              SCAN FOR {stampNum.toString()} STAMP{stampNum > 1 ? 'S' : ''}
            </Typography>
          </>
        )}
      </div>
    </FullScreenDialog>
  );
}
